import React from "react";
import { Box, Typography } from "@mui/material";

interface ShelfSectionProps {
  /** Section label, e.g. "Vorlagen" or "Offene Termine · Spengler". */
  title: string;
  /** Item count rendered after the title as "(n)". Omit to hide. */
  count?: number;
  /** Small leading icon rendered before the title. */
  icon?: React.ReactNode;
  /** Controls rendered at the right end of the header row (pickers, toggle, close). */
  headerExtras?: React.ReactNode;
  /** Minimum height of the card row (px) — shared by all sections. */
  rowHeight: number;
  /** Let the section take remaining band width (card rows scroll internally either way). */
  grow?: boolean;
  /**
   * Column-band mode (shelf in the patient drawer): cards wrap onto new lines
   * instead of scrolling horizontally, and the section spans the full width.
   * Wrapping rather than stacking handles both shapes of child — 210px cards
   * fall one per line in a 320px drawer, narrow template chips pair up.
   */
  wrap?: boolean;
  /** Card row content. */
  children: React.ReactNode;
}

/**
 * Layout primitive for one section of the planning shelf band (Vorlagen /
 * Offene Termine / Ablage). Renders a compact header line and a horizontally
 * scrolling card row. Deliberately has NO surface of its own — the band
 * provides the single shared background; sections are separated by thin
 * dividers.
 *
 * The row is `minHeight: rowHeight`, not a fixed height: a card with more
 * slots than the minimum fits grows the band a few px instead of getting an
 * unusable scrollbar inside a 210px card. The height is stable per patient —
 * it only varies with the data, not with interaction.
 */
const ShelfSection: React.FC<ShelfSectionProps> = ({
  title,
  count,
  icon,
  headerExtras,
  rowHeight,
  grow,
  wrap,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      px: 1,
      pt: 0.25,
      pb: 0.5,
      // Non-grow sections (Vorlagen) size to their content: there is plenty
      // of horizontal room, so squeezing them behind a scrollbar while the
      // grow section idles with spare width looked broken. The maxWidth guard
      // (plus the row's internal scroll) is the fallback for truly huge
      // content on narrow windows.
      flex: wrap ? "1 1 auto" : grow ? "1 1 0" : "0 0 auto",
      maxWidth: wrap || grow ? undefined : "60%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: 28, flexShrink: 0 }}>
      {icon}
      <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
        {title}
        {count != null ? ` (${count})` : ""}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {headerExtras}
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: wrap ? "wrap" : "nowrap",
        alignItems: "flex-start",
        gap: 1,
        minHeight: rowHeight,
        overflowX: wrap ? "visible" : "auto",
        // Slim symmetric padding — count badges render INSIDE the card
        // corners (overflow clipping ignores z-index, so overhanging badges
        // would need compensating padding here and cost vertical space).
        p: 0.5,
        // Always-visible thin scrollbar: macOS overlay scrollbars hide until
        // scrolled, so overflowing cards would otherwise go unnoticed.
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0, 0, 0, 0.35)",
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.08)",
          borderRadius: 3,
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

export default ShelfSection;
